import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import UniversalDocumentViewer from "@/components/documents/UniversalDocumentViewer";
import DocumentReviewPanel from "@/components/documents/DocumentReviewPanel";
import { ClassificationPanel } from "@/components/documents/ClassificationPanel";
import { RequirementMatchSection } from "@/components/documents/RequirementMatchSection";
import { TaxYearSection } from "@/components/documents/TaxYearSection";
import DocumentIntelligenceCard from "@/components/documents/DocumentIntelligenceCard";
import { ClientWorkspaceLink } from "@/components/shared";

const fmtDT = (d) =>
  d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const chip = (s) => (s || "").replace(/_/g, " ");
const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const IN_PROGRESS = ["uploaded", "security_scanning", "processing_preview", "extracting_text"];

// Upload source → human-readable label
const SOURCE_LABEL = {
  client_upload:  "Client Portal",
  portal_upload:  "Client Portal",
  staff_upload:   "Staff",
  comm_draft:     "Message Attachment",
  system:         "System",
};

// Canonical jurisdiction code → friendly display name (subset; complete list on backend)
const JX_DISPLAY = {
  us: "United States", US: "United States",
  uk: "United Kingdom", UK: "United Kingdom",
  th: "Thailand",      TH: "Thailand",
  "US-AL": "Alabama",          "US-AK": "Alaska",         "US-AZ": "Arizona",
  "US-AR": "Arkansas",         "US-CA": "California",     "US-CO": "Colorado",
  "US-CT": "Connecticut",      "US-DE": "Delaware",       "US-FL": "Florida",
  "US-GA": "Georgia",          "US-HI": "Hawaii",         "US-ID": "Idaho",
  "US-IL": "Illinois",         "US-IN": "Indiana",        "US-IA": "Iowa",
  "US-KS": "Kansas",           "US-KY": "Kentucky",       "US-LA": "Louisiana",
  "US-ME": "Maine",            "US-MD": "Maryland",       "US-MA": "Massachusetts",
  "US-MI": "Michigan",         "US-MN": "Minnesota",      "US-MS": "Mississippi",
  "US-MO": "Missouri",         "US-MT": "Montana",        "US-NE": "Nebraska",
  "US-NV": "Nevada",           "US-NH": "New Hampshire",  "US-NJ": "New Jersey",
  "US-NM": "New Mexico",       "US-NY": "New York",       "US-NC": "North Carolina",
  "US-ND": "North Dakota",     "US-OH": "Ohio",           "US-OK": "Oklahoma",
  "US-OR": "Oregon",           "US-PA": "Pennsylvania",   "US-RI": "Rhode Island",
  "US-SC": "South Carolina",   "US-SD": "South Dakota",   "US-TN": "Tennessee",
  "US-TX": "Texas",            "US-UT": "Utah",           "US-VT": "Vermont",
  "US-VA": "Virginia",         "US-WA": "Washington",     "US-WV": "West Virginia",
  "US-WI": "Wisconsin",        "US-WY": "Wyoming",
  "US-DC": "District of Columbia", "US-PR": "Puerto Rico",
  "US-VI": "U.S. Virgin Islands",  "US-GU": "Guam",
  "US-AS": "American Samoa",   "US-MP": "Northern Mariana Islands",
};
const jxName = (code) => JX_DISPLAY[code] || chip(code);

// Category keys that are upload-method signals, not document identity
const UPLOAD_TYPE_KEYS = new Set(["client_upload", "staff_upload", "portal_upload", "comm_draft"]);

const TECH_TONE = {
  available:   "bg-emerald-100 text-emerald-700",
  processing:  "bg-blue-100 text-blue-700",
  quarantined: "bg-rose-100 text-rose-700",
  failed:      "bg-rose-100 text-rose-700",
  degraded:    "bg-amber-100 text-amber-700",
};
const TECH_LABEL = {
  available: "Available", processing: "Processing",
  quarantined: "Security Hold", failed: "Failed", degraded: "Degraded",
};
const REVIEW_TONE = {
  unreviewed:          "bg-slate-100 text-slate-500",
  in_review:           "bg-amber-100 text-amber-700",
  accepted:            "bg-emerald-100 text-emerald-700",
  needs_replacement:   "bg-amber-100 text-amber-700",
  duplicate:           "bg-orange-100 text-orange-700",
  superseded:          "bg-slate-100 text-slate-400",
  rejected:            "bg-rose-100 text-rose-600",
  not_applicable:      "bg-slate-100 text-slate-400",
  needs_clarification: "bg-indigo-100 text-indigo-700",
};

function Section({ title, icon: Ic, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100" data-testid={testid}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between py-2.5 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
          {Ic && <Ic className="h-3 w-3" />}{title}
        </span>
        <Icons.ChevronDown className={`h-3 w-3 text-slate-300 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

function Row({ label, value }) {
  if (!value || value === "—") return null;
  return (
    <div className="flex justify-between gap-3 text-xs py-0.5">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className="text-slate-700 font-medium text-right truncate max-w-[60%]">{value}</span>
    </div>
  );
}

// Technical status checkmarks panel
function TechnicalStatusPanel({ c }) {
  const ps = c.processing_status || "";
  const ts = c.technical_status || "";
  const lc = c.lifecycle || {};
  const preview = c.preview || {};
  const textEx = c.text_extraction || {};

  const checks = [
    {
      key: "security",
      label: "Security Scan",
      done: ts !== "quarantined" && !["uploaded", "security_scanning"].includes(ps),
      warn: ts === "quarantined",
      pending: ["uploaded", "security_scanning"].includes(ps),
    },
    {
      key: "processing",
      label: "Processing Complete",
      done: ["available", "degraded"].includes(ts),
      warn: ts === "degraded",
      pending: ["processing", "uploading"].includes(ts),
      fail: ts === "failed",
    },
    {
      key: "preview",
      label: "Preview Generated",
      done: preview.available === true,
      fail: preview.state === "failed",
      pending: preview.state === "processing",
      skip: preview.kind === "none" || preview.state === "skipped",
    },
    {
      key: "text",
      label: "Text Extracted",
      done: textEx.available === true,
      skip: textEx.status === "skipped" || textEx.status === "not_applicable",
      pending: textEx.status === "processing",
    },
    {
      key: "ai",
      label: "AI Ready",
      done: lc.ai_ready?.ready === true,
      skip: false,
      pending: false,
    },
  ];

  return (
    <div className="space-y-1" data-testid="technical-status-panel">
      {checks.map(({ key, label, done, warn, pending, fail, skip }) => {
        let Icon = Icons.CheckCircle2;
        let cls = "text-emerald-500";
        if (skip)         { Icon = Icons.MinusCircle; cls = "text-slate-300"; }
        else if (fail)    { Icon = Icons.XCircle;     cls = "text-rose-500"; }
        else if (warn)    { Icon = Icons.AlertCircle; cls = "text-amber-500"; }
        else if (pending) { Icon = Icons.Clock;        cls = "text-blue-400"; }
        else if (!done)   { Icon = Icons.Circle;       cls = "text-slate-200"; }
        return (
          <div key={key} className="flex items-center gap-2 text-xs" data-testid={`tech-status-${key}`}>
            <Icon className={`h-3.5 w-3.5 shrink-0 ${cls}`} />
            <span className={done ? "text-slate-700" : skip ? "text-slate-300" : "text-slate-500"}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function DocumentViewerPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  // ── Inline rename state ──
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameErr, setRenameErr] = useState("");

  const load = useCallback(() => {
    setErr(false);
    api.get(`/documents-intel/${documentId}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [documentId]);
  useEffect(() => { load(); }, [load]);

  // Conservative polling only while processing is in-flight.
  const procStatus = d?.canonical?.processing_status;
  useEffect(() => {
    if (!IN_PROGRESS.includes(procStatus)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [procStatus, load]);

  const retry = useCallback(() => {
    api.post(`/documents-intel/${documentId}/reprocess`).then(load).catch(() => {});
  }, [documentId, load]);

  const startRename = useCallback((currentName) => {
    setRenameVal(currentName || "");
    setRenameErr("");
    setRenaming(true);
  }, []);

  const saveRename = useCallback(async () => {
    if (!renameVal.trim()) return;
    setRenameSaving(true);
    try {
      await api.patch(`/documents-intel/${documentId}/metadata`, { display_filename: renameVal.trim() });
      setRenaming(false);
      load();
    } catch (e) {
      setRenameErr(e.response?.data?.detail || "Rename failed.");
    }
    setRenameSaving(false);
  }, [documentId, renameVal, load]);

  const cancelRename = useCallback(() => {
    setRenaming(false);
    setRenameErr("");
  }, []);

  if (err) return <div className="p-10 text-center text-slate-400" data-testid="doc-viewer-error">Document not found.</div>;
  if (!d) return <div className="p-10 flex justify-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;

  const c = d.canonical || {};
  const doc = d.document || {};
  const vis = c.visibility || {};
  const downloadUrl = `${API}/documents-intel/${documentId}/content?disposition=attachment&auth=${encodeURIComponent(getToken() || "")}`;

  // Return-context resolution:
  // 1. Explicit caller state (validated against known internal prefixes) — highest priority
  // 2. Canonical fallback when document has a client (direct link / new tab)
  // 3. Firm-wide Documents fallback for unassigned documents
  const stateReturn = (() => {
    const s = location.state?.returnTo;
    if (!s) return null;
    if (s === "/documents") return s;
    if (s.startsWith("/client-workspace/")) return s;
    return null; // reject anything else (open-redirect guard)
  })();
  const docClientId = d?.client?.id || c?.client_id;
  const backTo = stateReturn ?? (docClientId ? `/client-workspace/${docClientId}?tab=documents` : "/documents");

  // Effective display type — don't show upload-source values as document identity
  const displayDocType = (!UPLOAD_TYPE_KEYS.has(c.document_type) && c.document_type) ? chip(c.document_type) : null;
  const displayCategory = (!UPLOAD_TYPE_KEYS.has(c.category) && c.category && c.category !== "other") ? chip(c.category) : null;
  const isTaxYearConfirmed = (c.staff_confirmed_metadata_fields || []).includes("tax_year");
  const taxYearDisplay = (c.tax_years || []).length > 0
    ? (c.tax_years || []).join(", ")
    : c.tax_year || (isTaxYearConfirmed ? "N/A" : null);
  const jurisdictionDisplay = (c.jurisdictions || []).map(jxName).filter(Boolean).join(", ") || null;
  const commRels = (doc?.relationships || []).filter(
    (r) => r.type === "historical_conversation" || r.type === "communication"
  );

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="document-viewer-page">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white shrink-0">
        <button onClick={() => navigate(backTo)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0" title="Back" data-testid="doc-viewer-back">
          <Icons.ArrowLeft className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1">
          {/* Filename — inline rename for staff */}
          {renaming ? (
            <div className="space-y-1" data-testid="doc-rename-form">
              <div className="flex items-center gap-1.5">
                <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <input
                  autoFocus
                  value={renameVal}
                  onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") cancelRename(); }}
                  className="flex-1 text-sm font-semibold border border-blue-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  data-testid="doc-rename-input"
                />
              </div>
              {renameErr && <div className="text-[11px] text-rose-600 pl-5" data-testid="doc-rename-error">{renameErr}</div>}
              <div className="flex items-center gap-1.5 pl-5">
                <button
                  onClick={saveRename}
                  disabled={!renameVal.trim() || renameSaving}
                  className="text-[11px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  data-testid="doc-rename-save"
                >
                  {renameSaving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={cancelRename}
                  className="text-[11px] px-2 py-0.5 border border-slate-200 text-slate-600 rounded hover:bg-slate-50"
                  data-testid="doc-rename-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="font-semibold text-slate-900 flex items-center gap-1.5 group" data-testid="doc-viewer-title">
              <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="truncate">{c.display_name || doc.original_filename}</span>
              {c.version > 1 && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 rounded shrink-0">v{c.version}</span>}
              <button
                onClick={() => startRename(c.display_name || doc.original_filename)}
                className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                title="Rename document"
                data-testid="doc-rename-btn"
              >
                <Icons.Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
          {/* Client identity — links to their workspace */}
          {d.client?.id && (
            <div className="mt-0.5" data-testid="doc-viewer-client-identity">
              <ClientWorkspaceLink
                clientId={d.client.id}
                clientName={d.client.full_name}
                clientNumber={d.client.client_number}
                className="text-xs"
                testId="doc-viewer-client-link"
              />
            </div>
          )}
          {/* Identity chips below filename */}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {displayCategory && (
              <span className="text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 capitalize" data-testid="header-category">
                {displayCategory}
              </span>
            )}
            {displayDocType && displayDocType !== displayCategory && (
              <span className="text-[10px] text-slate-600 bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 capitalize" data-testid="header-doc-type">
                {displayDocType}
              </span>
            )}
            {c.form_number && (
              <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-mono" data-testid="header-form-number">
                {c.form_number}
              </span>
            )}
            {taxYearDisplay && (
              <span className="text-[10px] bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5" data-testid="header-tax-year">
                TY {taxYearDisplay}
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${REVIEW_TONE[c.review_status] || "bg-slate-100 text-slate-500"}`} data-testid="doc-viewer-review-status">
              {chip(c.review_status) || "Unreviewed"}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${TECH_TONE[c.technical_status] || "bg-slate-100 text-slate-600"}`} data-testid="doc-viewer-technical-status">
              {TECH_LABEL[c.technical_status] || chip(c.technical_status) || chip(c.processing_status)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {c.lifecycle?.retryable === true && (
            <button onClick={retry} className="inline-flex items-center gap-1.5 text-sm border border-amber-300 text-amber-700 rounded-lg px-3 py-1.5 hover:bg-amber-50" data-testid="doc-viewer-retry">
              <Icons.RefreshCw className="h-4 w-4" />Retry
            </button>
          )}
          {docClientId && (
            <button
              onClick={() => navigate(`/client-workspace/${docClientId}`)}
              className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 text-slate-600"
              data-testid="doc-viewer-workspace-btn"
            >
              <Icons.Briefcase className="h-4 w-4" />Client Workspace
            </button>
          )}
          <a href={downloadUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 text-slate-600" data-testid="doc-viewer-download">
            <Icons.Download className="h-4 w-4" />Download
          </a>
        </div>
      </div>

      {/* ── Body: viewer + right rail ── */}
      <div className="flex-1 flex min-h-0">
        {/* Viewer */}
        <div className="flex-1 p-4 min-w-0">
          <UniversalDocumentViewer docId={documentId} preview={c.preview} processingStatus={c.processing_status} displayName={c.display_name} onRetry={retry} className="h-full" />
        </div>

        {/* Right Rail */}
        <aside className="w-80 shrink-0 border-l border-slate-200 bg-white overflow-y-auto px-4 pb-8" data-testid="doc-viewer-rail">

          {/* 1. Tax Year — first staff decision */}
          <Section title="Tax Year" icon={Icons.CalendarDays} testid="rail-tax-year">
            <TaxYearSection
              docId={documentId}
              taxYear={c.tax_year}
              taxYears={c.tax_years}
              staffConfirmedFields={c.staff_confirmed_metadata_fields}
              onRefresh={load}
            />
          </Section>

          {/* 2. Classification — what is this document? Auto-collapses when confirmed so rail focus shifts to Requirement Match */}
          <Section
            key={d.has_confirmed_classification ? "cls-confirmed" : "cls-unconfirmed"}
            title="Classification"
            icon={Icons.Tag}
            testid="rail-classification"
            defaultOpen={!d.has_confirmed_classification}
          >
            <ClassificationPanel
              docId={documentId}
              onRefresh={load}
              jurisdictions={c.jurisdictions}
              category={c.category}
              documentType={c.document_type}
              formNumber={c.form_number}
              filingAuthority={c.filing_authority}
            />
          </Section>

          {/* 3. Requirement Match — consolidated (replaces Organizer Matching + Phase 1C + Phase 2C panels) */}
          {/* key: remounts open when pending matches appear; defaultOpen: collapses when all matches resolved */}
          {docClientId && (
            <Section
              key={d.has_pending_matches ? "has-matches" : "no-matches"}
              title="Requirement Match"
              icon={Icons.GitCompareArrows}
              testid="rail-requirement-match"
              defaultOpen={!!d.has_pending_matches}
            >
              <RequirementMatchSection docId={documentId} onRefresh={load} />
            </Section>
          )}

          {/* 4. Document Review — quality verdict: is this document usable? */}
          {/* key: remounts when accepted state changes; defaultOpen: collapses only when accepted */}
          <Section
            key={`document-review-${c.review_status === "accepted" ? "accepted" : "active"}`}
            title="Document Review"
            icon={Icons.ClipboardCheck}
            testid="rail-review"
            defaultOpen={c.review_status !== "accepted"}
          >
            <DocumentReviewPanel docId={documentId} reviewStatus={c.review_status} onChange={load} onDisregard={() => navigate(-1)} />
          </Section>

          {/* 5. Document Intelligence */}
          {/* auto-open when intelligence has been generated (status=ready); collapse when not yet generated */}
          <Section
            key={`intelligence-${d.has_intelligence ? "ready" : "pending"}`}
            title="Document Intelligence"
            icon={Icons.Sparkles}
            testid="rail-intelligence"
            defaultOpen={!!d.has_intelligence}
          >
            <DocumentIntelligenceCard docId={documentId} />
          </Section>

          {/* ── Reference / Diagnostic (all collapsed by default) ── */}

          {/* 6. Technical Status */}
          <Section title="Technical Status" icon={Icons.Cpu} testid="rail-technical" defaultOpen={false}>
            <TechnicalStatusPanel c={c} />
          </Section>

          {/* 7. Document Details */}
          <Section title="Document Details" icon={Icons.Info} testid="rail-details" defaultOpen={false}>
            <Row label="Format" value={c.file_extension?.toUpperCase()} />
            <Row label="File Size" value={fmtSize(c.file_size_bytes)} />
            <Row label="Uploaded Via" value={SOURCE_LABEL[c.source] || chip(c.source)} />
            <Row label="Uploaded By" value={c.uploaded_by_name} />
            <Row label="Uploaded" value={fmtDT(c.uploaded_at)} />
            {c.original_filename && c.original_filename !== c.display_name && (
              <Row label="Original Filename" value={c.original_filename} />
            )}
            {c.form_number && <Row label="Form" value={c.form_number} />}
            {c.filing_authority && <Row label="Filing Authority" value={c.filing_authority} />}
            {jurisdictionDisplay && <Row label="Jurisdiction" value={jurisdictionDisplay} />}
            {c.related_person && <Row label="Related Person" value={c.related_person} />}
            {c.related_entity && <Row label="Related Entity" value={c.related_entity} />}
          </Section>

          {/* 8. Source Communication (conditional) */}
          {commRels.length > 0 && (
            <Section title="Source Communication" icon={Icons.MessageSquare} testid="rail-source-comm" defaultOpen={false}>
              <div className="space-y-3">
                {commRels.map((rel) => (
                  <div key={rel.id} className="space-y-1" data-testid={`rail-source-comm-${rel.id}`}>
                    <div className="flex items-start gap-2">
                      {rel.type === "historical_conversation"
                        ? <Icons.History className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                        : <Icons.MessageSquare className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-slate-700 font-medium truncate" data-testid={`rail-source-comm-label-${rel.id}`}>
                          {rel.label || (rel.type === "historical_conversation" ? "Historical Correspondence" : "Communication")}
                        </div>
                        <div className="text-[10px] text-slate-400 capitalize">
                          {rel.type === "historical_conversation" ? "Historical Correspondence" : "Live Communication"}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={rel.type === "historical_conversation"
                        ? `/client-workspace/${c.client_id}`
                        : `/communications`}
                      className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 pl-5"
                      data-testid={`rail-source-comm-link-${rel.id}`}
                    >
                      <Icons.ExternalLink className="h-3 w-3" />View Source Communication
                    </Link>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* 9. Client Visibility */}
          <Section title="Client Visibility" icon={Icons.Eye} testid="rail-visibility" defaultOpen={false}>
            <Row label="Client visible" value={vis.client_visible ? "Yes" : "No"} />
            <Row label="Staff visible" value={vis.staff_visible ? "Yes" : "No"} />
            <Row label="Management only" value={vis.management_only ? "Yes" : "No"} />
          </Section>

          {/* 10. Version History (conditional) */}
          {(d.version_history || []).length > 1 && (
            <Section title="Version History" icon={Icons.GitBranch} testid="rail-versions" defaultOpen={false}>
              <div className="space-y-1">
                {d.version_history.map((v) => (
                  <div key={v.id} className="flex justify-between text-xs">
                    <span className="text-slate-700">v{v.version} · {chip(v.status)} {v.is_current && <span className="text-emerald-600">(current)</span>}</span>
                    <span className="text-slate-400">{fmtDT(v.created_at)}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* 11. Audit Summary (conditional) */}
          {(d.timeline || []).length > 0 && (
            <Section title="Audit Summary" icon={Icons.History} testid="rail-audit" defaultOpen={false}>
              <div className="space-y-1.5">
                {d.timeline.slice(0, 6).map((t, i) => (
                  <div key={i} className="text-xs">
                    <div className="text-slate-700">{t.title}</div>
                    <div className="text-slate-400 text-[10px]">{fmtDT(t.created_at)}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </aside>
      </div>
    </div>
  );
}
